/* file description */

import { useEffect, useState } from 'react';
import { UseStoreProps } from './type';
// import { compose } from '@cui/corteza-js';
import _ from 'lodash';
import { useDebounceEffect, useDeepCompareEffect, useSetState } from 'ahooks';
import { EMPTY_MODULE_ID } from './consts';
import { getPrimaryKeyOfModule } from '@/utils';
import useValuesStore from '@/store/useValuesStore';
// import { composeRecord } from '@/utils';

export const isEmptyModule = (module: any) => {
  return module.moduleID === EMPTY_MODULE_ID;
};

export const useStore = ({
  tableBaseMode, // 当前表格模式，编辑模式下获取全量数据并取消分页功能；展示模式下默认分页
  value,
  request,
  namespaceID,
  moduleID,
  filter,
  moduleInfo,
  requestParams,
  onChange,
  rowKey,
  onSearch,
  searchParams,
  sorter: propsSorter,
  defaultPagination,
}: UseStoreProps) => {
  const { realoadValue } = useValuesStore();
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterSql, setFilterSql] = useState<string>('');
  const [page, setPage] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tabelInfo, setTableInfo] = useSetState<any>(Object.assign({
    params: searchParams || {},
    sorter: {},
    current: 1,
    pageSize: 10,
    total: 0,
  }, defaultPagination));

  const getData = (current?: number, pageSize?: number, sort?: any) => {
    const primaryKey = getPrimaryKeyOfModule(moduleInfo) || '';
    if (request) {
      setLoading(true);
      request({
        namespaceID,
        moduleID,
        filter,
        moduleInfo,
        requestParams,
        current: current || tabelInfo.current,
        pageSize: pageSize || tabelInfo.pageSize,
        ...tabelInfo.params,
      }, sort || (tabelInfo.sorter?.field ? tabelInfo.sorter : propsSorter)).then((res: { data: any[]; success: boolean; total: number; pFilter: Record<any, any> }) => {
        setLoading(false);
        const data = (res.data ?? []).map((x) => ({
          ...x,
          [rowKey!]: x[primaryKey] || _.uniqueId(), // _id数据默认为主键数据
        }));
        setTableInfo({
          current: current || tabelInfo.current,
          pageSize: pageSize || tabelInfo.pageSize,
          total: res.total,
        });
        setRowsData(data);
        setFilterSql(res?.pFilter?.query);
        onChange && onChange(data);
      }).catch(() => {
        setLoading(false);
      });
    } else {
      setTableInfo({
        current: tabelInfo.current,
        pageSize: tabelInfo.pageSize,
      });
    }
  };

  // 非受控模式
  useEffect(() => {
    // value 存在时为受控模式
    if (namespaceID && moduleID && !isEmptyModule(moduleInfo) && value === undefined) {
      // if (tableBaseMode === 'edit') {
      //   getData(1, 1000);
      // } else {
      //   getData();
      // }
      getData();
    }
  }, [namespaceID, moduleID, filter, moduleInfo, JSON.stringify(requestParams), tabelInfo.params]);

  // 受控模式
  useEffect(() => {
    if (_.isArray(value)) {
      setLoading(false);
      value.forEach(row => {
        if (rowKey && row && !row[rowKey]) {
          row[rowKey] = _.uniqueId();
        }
      });
      setRowsData(value);
    }
  }, [value]);

  useDeepCompareEffect(() => {
    setTableInfo({
      ...tabelInfo,
      params: searchParams || {},
    });
  }, [searchParams]);

  // 添加数据，支持单条或者多条
  const addEditRecord = (values?: object | any[]) => {
    let addValues: any[];
    if (_.isArray(values)) {
      addValues = [...values];
    } else if (values) {
      addValues = [values];
    } else {
      addValues = [{}];
    }
    const newRecords = addValues.map((v) => {
      const key = rowKey;
      const record: Record<any, any> = v;
      // const module = moduleInfo.clone();
      // record.RECORD = new compose.Record(module, record);
      // Object.keys(record.RECORD.values).forEach((item) => {
      //   if (record.RECORD.values[item] !== undefined && item !== 'toJSON') {
      //     record[item] = record.RECORD.values[item];
      //   }
      // });
      // const module = moduleInfo.clone();
      // const module = moduleInfo;
      // const RECORD = new compose.Record(module, record);
      // const RECORD = composeRecord(record);
      // Object.keys(RECORD).forEach((item) => {
      //   if (RECORD.values[item] !== undefined && item !== 'toJSON') {
      //     record[item] = RECORD.values[item];
      //   }
      // });
      // 新增时组件要求key值唯一
      record[key] = (Math.random() + new Date().getTime()).toString(32);
      return record;
    });
    const newRowsData = [...rowsData, ...newRecords];
    setRowsData(newRowsData);
    onChange && onChange(newRowsData);
  };

  const reset = () => {
    setRowsData([]);
  };

  const setRowData = (index: number, changeRowVal: Record<string, any>) => {
    const newRowsData = _.cloneDeep(rowsData);
    if (newRowsData[index]) {
      newRowsData[index] = { ...newRowsData[index], ...changeRowVal };
      setRowsData(newRowsData);
      onChange && onChange(newRowsData);
    }
  };

  return {
    rowsData,
    reset,
    setRowData,
    setRowsData: (data: any) => {
      setRowsData(data ?? []);
    },
    filterSql,
    addEditRecord,
    reload: () => {
      getData();
    },
    pagination: {
      current: tabelInfo.current,
      pageSize: tabelInfo.pageSize,
      total: !value ? tabelInfo.total : null,
    },
    setParams: (p: any) => {
      onSearch && onSearch(p);
      setTableInfo({
        ...tabelInfo,
        params: p,
        current: 1,
      });
    },
    searchParams: tabelInfo.params,
    setTableInfo: (
      _page: { current: number; pageSize: number },
      _filters: any,
      _sort?: { field?: string; order?: 'descend' | 'ascend' },
    ) => {
      const sorter = (_sort?.field && _sort.order) ? _sort : {};
      setTableInfo({
        ...tabelInfo,
        current: _page.current,
        pageSize: _page.pageSize,
        sorter,
      });
      // 非受控
      if (!value) {
        getData(_page.current, _page.pageSize, (_sort?.field && _sort.order) ? _sort : (propsSorter || {}));
      }
      // 受控模式下，详情页子表排序
      if (value && moduleID && (sorter.field !== tabelInfo.sorter?.field || sorter.order !== tabelInfo.sorter?.order)) {
        realoadValue(moduleID, (_sort?.field && _sort.order) ? _sort : (propsSorter || {}));
      }
    },
    loading,
  };
};
